import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  pendingChanges: {
    notesChanged: boolean;
    codeChanged: boolean;
    ministersAdded: number;
    ministersRemoved: number;
  };
}

/**
 * Organism: Dialog warning about unsaved changes before navigation
 */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  pendingChanges,
}: UnsavedChangesDialogProps) {
  const hasChanges = 
    pendingChanges.notesChanged || 
    pendingChanges.codeChanged || 
    pendingChanges.ministersAdded > 0 || 
    pendingChanges.ministersRemoved > 0;

  if (!hasChanges) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="size-5 text-amber-600" />
            </div>
            <div>
              <DialogTitle>Cambios sin guardar</DialogTitle>
              <DialogDescription>
                Tiene cambios sin guardar que se perderán si continúa
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm font-medium">Cambios pendientes:</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {pendingChanges.notesChanged && (
              <li className="flex items-start gap-2">
                <span className="mt-0.5 size-1.5 rounded-full bg-amber-500" />
                <span>Notas modificadas</span>
              </li>
            )}
            {pendingChanges.codeChanged && (
              <li className="flex items-start gap-2">
                <span className="mt-0.5 size-1.5 rounded-full bg-amber-500" />
                <span>Código modificado</span>
              </li>
            )}
            {pendingChanges.ministersAdded > 0 && (
              <li className="flex items-start gap-2">
                <span className="mt-0.5 size-1.5 rounded-full bg-amber-500" />
                <span>
                  {pendingChanges.ministersAdded} ministro{pendingChanges.ministersAdded !== 1 ? 's' : ''} agregado{pendingChanges.ministersAdded !== 1 ? 's' : ''}
                </span>
              </li>
            )}
            {pendingChanges.ministersRemoved > 0 && (
              <li className="flex items-start gap-2">
                <span className="mt-0.5 size-1.5 rounded-full bg-amber-500" />
                <span>
                  {pendingChanges.ministersRemoved} ministro{pendingChanges.ministersRemoved !== 1 ? 's' : ''} removido{pendingChanges.ministersRemoved !== 1 ? 's' : ''}
                </span>
              </li>
            )}
          </ul>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
          >
            Salir sin guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
